import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, takeUntil } from 'rxjs/operators';
import { SurveysService } from '@src/app/shared/backend/clients/api/surveys.service';
import { FilterOperator } from '@app/shared/enums/FilterOperator.enum';

@Component({
    selector: 'app-survey-selector',
    templateUrl: './survey-selector.component.html',
    styleUrl: './survey-selector.component.scss',
    host: {
        class: 'app-survey-selector'
    },
    standalone: false
})
export class SurveySelectorComponent implements OnInit, OnDestroy {

  hasLoadedSurveys = false;
  surveys: any[] = [];

  surveyTotalCount = 0;
  surveyTotalPages = 1;
  surveyRowsPerPageOption = 10;

  surveyFormGroup: FormGroup;

  private readonly componentDestroyed$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private surveysService: SurveysService
  ) {
    this.surveyFormGroup = this.fb.group({
      _survey: [[]]
    });
  }

  ngOnInit(): void {
    this.surveyFormGroup.get('_survey')!.valueChanges.pipe(
      map((value: number[] | number | null) => Array.isArray(value) ? value[0] : value),
      filter((surveyId): surveyId is number => !!surveyId),
      distinctUntilChanged(),
      takeUntil(this.componentDestroyed$)
    ).subscribe((surveyId: number) => {
      this.router.navigate(['/surveys', surveyId, 'take-survey']);
    });

    this.loadSurveys();
  }

  onLazyLoadSurveys(event: any): void {
    this.loadSurveys(event);
  }

  loadSurveys(event?: any): void {
    const search = event?.search?.trim() || null;
    const activePage = Math.max(1, (event?.pageIndex ?? 1));
    const pageSize = event?.pageSize ?? this.surveyRowsPerPageOption;

    const additionalFilters: any[] = [
      {
        propertyName: 'isPublished',
        operator: FilterOperator.EQUALS,
        values: ['true']
      },
      {
        propertyName: 'isActive',
        operator: FilterOperator.EQUALS,
        values: ['true']
      }
    ];

    if (search) {
      additionalFilters.push({
        propertyName: 'name',
        operator: FilterOperator.CONTAINS,
        values: [search]
      });
    }

    const payload = {
      additionalFilters,
      pageNumber: activePage,
      pageSize
    };

    this.surveysService.apiSurveysFilterComboPost(payload).subscribe({
      next: (response) => {
        this.surveys = [...(response.results ?? [])];
        this.surveyTotalCount = response.totalCount ?? 0;
        this.surveyTotalPages = Math.max(1, Math.ceil(this.surveyTotalCount / pageSize));
        this.hasLoadedSurveys = true;
      },
      error: (err) => {
        console.error('Error fetching surveys:', err);
        this.surveys = [];
        this.surveyTotalCount = 0;
        this.surveyTotalPages = 1;
        this.hasLoadedSurveys = false;
      }
    });
  }

  ngOnDestroy(): void {
    this.componentDestroyed$.next();
    this.componentDestroyed$.complete();
  }
}
